import React, { useState } from "react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Spinner,
} from "@heroui/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { Check, X, Warning, ArrowRight } from "@phosphor-icons/react";

interface ChangeUnitIdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newUnitId: string) => Promise<void>;
  currentUnitId: string;
  buildingId: string;
  isLoading?: boolean;
}

export const ChangeUnitIdModal: React.FC<ChangeUnitIdModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentUnitId,
  buildingId,
  isLoading = false,
}) => {
  const [newUnitId, setNewUnitId] = useState<string>("");
  const [error, setError] = useState<string>("");

  const handleClose = () => {
    setNewUnitId("");
    setError("");
    onClose();
  };

  const validateUnitId = (id: string): string | null => {
    if (!id.trim()) {
      return "Unit ID is verplicht";
    }
    if (id === currentUnitId) {
      return "Nieuwe unit ID moet verschillen van de huidige";
    }
    // Check for valid characters
    if (!/^[A-Za-z0-9\-_]+$/.test(id)) {
      return "Unit ID mag alleen letters, cijfers, streepjes en underscores bevatten";
    }
    return null;
  };

  const handleConfirm = async () => {
    const validationError = validateUnitId(newUnitId);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    await onConfirm(newUnitId);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      isDismissable={!isLoading}
      classNames={{
        backdrop: "bg-black/50",
        base: "border border-neutral-primary/20",
        header: "border-b border-neutral-primary/20",
        footer: "border-t border-neutral-primary/20",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <h3 className="text-neutral-primary text-xl font-semibold">
            Wijzig Unit ID
          </h3>
          <p className="text-neutral-primary/70 text-sm font-normal">
            Wijzig de ID van deze unit. Dit past alle gerelateerde records aan.
          </p>
        </ModalHeader>

        <ModalBody className="py-6">
          <div className="space-y-4">
            {/* Warning message */}
            <div className="bg-status-warning/10 border-status-warning/30 flex gap-3 rounded-lg border p-4">
              <Warning
                size={24}
                className="text-status-warning mt-0.5 flex-shrink-0"
              />
              <div className="text-neutral-primary/80 text-sm">
                <p className="text-status-warning mb-1 font-semibold">
                  Let op: Admin-only actie
                </p>
                <p>
                  Dit wijzigt de unit ID in alle gerelateerde records inclusief
                  huurcontracten, helpdesk tickets, bestanden en SharePoint
                  folders. Deze actie kan niet ongedaan worden gemaakt.
                </p>
              </div>
            </div>

            {/* Current Unit Info */}
            <div className="bg-secondary-moss/10 space-y-3 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-primary font-medium">
                  Gebouw:
                </span>
                <span className="text-neutral-primary/80">{buildingId}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-primary font-medium">
                  Huidige Unit ID:
                </span>
                <span className="text-neutral-primary/80 font-mono">
                  {currentUnitId}
                </span>
              </div>
            </div>

            {/* ID Change Preview */}
            <div className="flex items-center justify-center gap-4 py-2">
              <div className="rounded-lg bg-gray-100 px-4 py-2 font-mono text-sm">
                {currentUnitId}
              </div>
              <ArrowRight size={20} className="text-neutral-primary/50" />
              <div
                className={`rounded-lg px-4 py-2 font-mono text-sm ${newUnitId ? "bg-moss-primary/20 text-moss-primary" : "text-neutral-primary/50 bg-gray-100"}`}
              >
                {newUnitId || "Nieuwe ID..."}
              </div>
            </div>

            {/* Input for new Unit ID */}
            <div>
              <label className="text-neutral-primary mb-2 block text-sm font-medium">
                Nieuwe Unit ID
              </label>
              <Input
                value={newUnitId}
                onChange={(e) => {
                  // Filter out invalid characters (only allow letters, numbers, dashes, underscores)
                  const filtered = e.target.value.replace(/[^A-Za-z0-9\-_]/g, '');
                  setNewUnitId(filtered);
                  setError("");
                }}
                placeholder="Voer de nieuwe unit ID in..."
                isDisabled={isLoading}
                classNames={{
                  input: "font-mono",
                  inputWrapper: error ? "border-status-bad" : "",
                }}
              />
              <p className="text-neutral-primary/50 mt-1 text-xs">
                Alleen letters, cijfers, streepjes (-) en underscores (_) toegestaan
              </p>
              {error && <p className="text-status-bad mt-1 text-sm">{error}</p>}
            </div>

            {/* Info about what will be updated */}
            <div className="text-neutral-primary/60 space-y-1 text-sm">
              <p className="font-medium">
                De volgende records worden bijgewerkt:
              </p>
              <ul className="list-inside list-disc space-y-0.5 text-xs">
                <li>Huurcontracten en aanvragen</li>
                <li>Helpdesk tickets</li>
                <li>Bestanden en SharePoint paden</li>
                <li>Assets en apparaten</li>
                <li>Geschiedenis logs</li>
                <li>En meer...</li>
              </ul>
            </div>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="flex w-full gap-3">
            <ButtonNew
              label="Annuleren"
              variant="secondaryNeutral"
              icon={<X size={16} />}
              onClick={handleClose}
              className="flex-1"
              isDisabled={isLoading}
            />
            <ButtonNew
              label={isLoading ? "Bezig met wijzigen..." : "Wijzig Unit ID"}
              variant="primaryMoss"
              icon={
                isLoading ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  <Check size={16} />
                )
              }
              onClick={handleConfirm}
              className="flex-1"
              isDisabled={isLoading || !newUnitId.trim()}
            />
          </div>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangeUnitIdModal;
